import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, CalendarClock, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { mailbox, mailErrorMessage } from "../../api/mailbox";
import type { MailScheduled } from "../../api/types";

// 「已排程」清單(規格 §2.6 排程寄送):系統保管、還沒寄出的信。寄出前可改時間、可取消。
// 背景已經搶去寄了(409 MAILBOX_SCHEDULE_LOCKED)就照後端那句話講,並重抓清單。

/** 台灣時間與 UTC 差 8 小時(台灣沒有日光節約時間)。 */
const TAIPEI_OFFSET_MS = 8 * 60 * 60 * 1000;
const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * `<input type="datetime-local">` 的值 → 帶時區的 ISO(UTC)。
 *
 * 欄位上寫的是**台灣時間**,不管這台電腦設在哪個時區(出差在外地的筆電、時區設錯的電腦):
 * 「09:30」一律是台北的 09:30(+08:00)。看不懂的值回 null。
 */
export function taipeiInputToIso(value: string): string | null {
  const m = LOCAL_INPUT.exec((value || "").trim());
  if (!m) return null;
  const utc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0) - TAIPEI_OFFSET_MS;
  const at = new Date(utc);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** ISO → `<input type="datetime-local">` 的值(台灣時間)。 */
export function isoToTaipeiInput(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const t = new Date(at.getTime() + TAIPEI_OFFSET_MS);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}` +
    `T${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}`
  );
}

/** 排程時間的檢查(寫信區與已排程清單共用)。可以用回 null,否則回要顯示的那一句。 */
export function scheduleTimeError(iso: string | null): string | null {
  if (!iso) return "請選要寄出的時間";
  if (new Date(iso).getTime() <= Date.now()) return "預定時間要在未來";
  return null;
}

export function ScheduledList({ onBack, refreshKey = 0 }: { onBack: () => void; refreshKey?: number }) {
  const toast = useToast();
  const [rows, setRows] = useState<MailScheduled[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const [timeError, setTimeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const got = await mailbox.scheduled();
      setRows(Array.isArray(got) ? got : []);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const reschedule = async (row: MailScheduled) => {
    const iso = taipeiInputToIso(when);
    const problem = scheduleTimeError(iso);
    setTimeError(problem);
    if (problem || !iso) return;
    setBusy(true);
    try {
      await mailbox.reschedule(row.id, iso);
      toast.push(`改好了,會在 ${formatTaipeiDateTime(iso)} 寄出`, "success");
      setEditing(null);
      setRows((prev) => (prev ?? []).map((one) => (one.id === row.id ? { ...one, send_at: iso } : one)));
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
      void load();
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (row: MailScheduled) => {
    if (!window.confirm(`取消排程「${row.subject || "(無主旨)"}」?這封信不會寄出。`)) return;
    setBusy(true);
    try {
      await mailbox.cancelScheduled(row.id);
      toast.push("已取消排程", "success");
      setRows((prev) => (prev ?? []).filter((one) => one.id !== row.id));
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
      void load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="已排程的信"
      className="flex min-w-0 flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 flex items-center gap-1.5 text-base font-semibold text-[var(--text)]">
          <CalendarClock aria-hidden="true" size={16} />
          已排程
        </h3>
        <Button
          size="sm"
          variant="ghost"
          data-back=""
          icon={<ArrowLeft aria-hidden="true" size={16} />}
          onClick={onBack}
        >
          回列表
        </Button>
      </div>
      {failed ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          讀不到排程的信,請稍後再試。
        </p>
      ) : rows === null ? (
        <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      ) : rows.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">沒有排程中的信。</p>
      ) : (
        <ul className="m-0 flex flex-col gap-2 p-0">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex list-none flex-col gap-1.5 rounded-[var(--radius)] border border-[var(--border)] px-3 py-2"
            >
              <span className="text-sm font-medium text-[var(--text)]">{row.subject || "(無主旨)"}</span>
              <span className="text-xs text-[var(--text-2)]">給 {[...row.to, ...row.cc].join("、") || "(密件副本)"}</span>
              <span className="text-xs text-[var(--text-2)]">
                預定 <span className="tabular">{formatTaipeiDateTime(row.send_at)}</span> 寄出
                {row.attachments.length > 0 ? ` · ${row.attachments.length} 個附件` : ""}
              </span>
              {editing === row.id ? (
                <div className="flex flex-wrap items-end gap-2">
                  <Input
                    label="新的寄出時間(台灣時間)"
                    type="datetime-local"
                    value={when}
                    error={timeError}
                    errorRole="alert"
                    onChange={(e) => {
                      setWhen(e.target.value);
                      setTimeError(null);
                    }}
                  />
                  <Button size="sm" variant="primary" loading={busy} disabled={!when} onClick={() => void reschedule(row)}>
                    儲存時間
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                    不改了
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      setEditing(row.id);
                      setTimeError(null);
                      setWhen(isoToTaipeiInput(row.send_at));
                    }}
                  >
                    改時間
                  </Button>
                  <Button size="sm" disabled={busy} icon={<X aria-hidden="true" size={14} />} onClick={() => void cancel(row)}>
                    取消排程
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
